import { pointerWithin } from '@dnd-kit/core'
import classNames from 'classnames'

import { EMPTY_OBJECT } from '../../constants/common'
import { DragDropProvider } from '../DragDrop'

import { CalendarGridContext } from './CalendarGrid.context'
import { useCalendarGridDragDrop } from './CalendarGrid.hooks'
import { CalendarGridAgenda } from './CalendarGridAgenda'
import { CalendarGridBodyMessage } from './CalendarGridBodyMessage'
import { CalendarGridChip } from './CalendarGridChip'
import { CalendarGridMonth } from './CalendarGridMonth'
import { CalendarGridNav } from './CalendarGridNav'
import { CalendarGridWeek } from './CalendarGridWeek'

import * as styles from './CalendarGrid.module.css'

function CalendarGrid({
  DayPopoverActions,
  ItemCard,
  ItemRow,
  ItemStatus,
  ItemTag,
  bodyMessage,
  calendar: {
    anchor,
    dayLoads,
    days,
    handleChangeMode,
    handleGoToNext,
    handleGoToPrev,
    handleGoToToday,
    isAgendaMode,
    isMonthMode,
    isTodayVisible,
    items,
    locale,
    mode,
    modes,
    now,
    today,
    weekdays,
  } = EMPTY_OBJECT,
  isLoading,
  onClickEmptyCell,
  onClickItem,
  onClickSecondaryLabel,
  onMoveItem,
  onMoveItemTime,
  onSelectEmptyRange,
}) {
  const { handleDragCancel, handleDragEnd, handleDragMove, previewItem } =
    useCalendarGridDragDrop({ onMoveItem, onMoveItemTime })

  function renderDraggedChip(activeId, activeData) {
    return activeData?.item ? <CalendarGridChip item={activeData.item} /> : null
  }

  return (
    <CalendarGridContext
      value={{ DayPopoverActions, ItemCard, ItemRow, ItemStatus, ItemTag }}
    >
      <div className={styles.CalendarGrid}>
        <CalendarGridNav
          anchor={anchor}
          days={days}
          isAgendaMode={isAgendaMode}
          isTodayVisible={isTodayVisible}
          locale={locale}
          mode={mode}
          modes={modes}
          onChangeMode={handleChangeMode}
          onGoToNext={handleGoToNext}
          onGoToPrev={handleGoToPrev}
          onGoToToday={handleGoToToday}
        />
        <div className={styles.body}>
          <DragDropProvider
            className={classNames(styles.board, {
              [styles.loading]: isLoading,
            })}
            collisionDetection={pointerWithin}
            dndContextProps={{ onDragMove: handleDragMove }}
            renderOverlay={renderDraggedChip}
            onDragCancel={handleDragCancel}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragMove}
          >
            {isAgendaMode ? (
              <CalendarGridAgenda
                days={days}
                emptyMessage={bodyMessage}
                items={items}
                today={today}
                onClickItem={onClickItem}
                onClickSecondaryLabel={onClickSecondaryLabel}
              />
            ) : isMonthMode ? (
              <CalendarGridMonth
                canMoveItems={Boolean(onMoveItem)}
                dayLoads={dayLoads}
                days={days}
                items={items}
                today={today}
                weekdays={weekdays}
                onClickItem={onClickItem}
                onClickSecondaryLabel={onClickSecondaryLabel}
              />
            ) : (
              <CalendarGridWeek
                canMoveItems={Boolean(onMoveItem)}
                canMoveItemsInTime={Boolean(onMoveItemTime)}
                dayLoads={dayLoads}
                days={days}
                items={items}
                now={now}
                previewItem={previewItem}
                today={today}
                onClickEmptyCell={onClickEmptyCell}
                onClickItem={onClickItem}
                onClickSecondaryLabel={onClickSecondaryLabel}
                onSelectEmptyRange={onSelectEmptyRange}
              />
            )}
          </DragDropProvider>
          {bodyMessage && !isAgendaMode ? (
            <CalendarGridBodyMessage
              {...bodyMessage}
              className={styles.bodyMessage}
            />
          ) : null}
        </div>
      </div>
    </CalendarGridContext>
  )
}

export default CalendarGrid
